import { supabase } from "./supabase";

export const STATUSES = ["pending", "approved", "rejected", "hold", "blocked", "inactive"];

export function canDo(auth, perm) {
  if (auth.role === "admin") return true;
  if (auth.role !== "sub_admin") return false;
  return !!auth.permissions?.[perm];
}

// Every staff page needs can_view; `perm` may be a string, array (any of) or "admin".
export function allowed(auth, perm) {
  if (perm === "admin") return auth.role === "admin";
  if (!canDo(auth, "can_view")) return false;
  if (!perm) return true;
  return (Array.isArray(perm) ? perm : [perm]).some((p) => canDo(auth, p));
}

export const emptyFilters = {
  q: "", status: "", gender: "", age_min: "", age_max: "", religion: "", caste: "", education: "", city: "", from: "", to: "",
};

const safe = (v) => String(v).replace(/[,()*%\\:"]/g, " ").trim();

export function applyFilters(query, f, base = {}) {
  let q = query;
  const term = f.q ? safe(f.q) : "";
  if (term) q = q.or(`full_name.ilike.*${term}*,registration_no.ilike.*${term}*,phone.ilike.*${term}*,whatsapp.ilike.*${term}*,email.ilike.*${term}*`);
  const status = base.status || f.status;
  if (status) q = q.eq("status", status);
  if (f.gender) q = q.eq("gender", f.gender);
  if (f.age_min) q = q.gte("age", Number(f.age_min));
  if (f.age_max) q = q.lte("age", Number(f.age_max));
  if (f.religion) q = q.eq("religion", f.religion);
  if (f.caste) q = q.ilike("caste", `%${safe(f.caste)}%`);
  if (f.education) q = q.ilike("highest_qualification", `%${safe(f.education)}%`);
  if (f.city) q = q.ilike("location_text", `%${safe(f.city)}%`);
  if (f.from) q = q.gte("created_at", `${f.from}T00:00:00+05:30`);
  if (f.to) q = q.lte("created_at", `${f.to}T23:59:59+05:30`);
  if (base.profile_for) q = q.eq("profile_for", base.profile_for);
  if (base.excludeId) q = q.neq("id", base.excludeId);
  if (base.hasUser) q = q.not("user_id", "is", null);
  return q;
}

export async function fetchProfiles({ filters, base, sort, page, pageSize }) {
  let q = supabase.from("admin_profile_list").select("*", { count: "exact" });
  q = applyFilters(q, filters, base).order(sort.key, { ascending: sort.dir === "asc" });
  const fromIdx = (page - 1) * pageSize;
  const { data, error, count } = await q.range(fromIdx, fromIdx + pageSize - 1);
  if (error) throw error;
  return { rows: data, count: count || 0 };
}

export async function fetchAllProfiles(filters, base, sort) {
  let q = supabase.from("admin_profile_list").select("*");
  q = applyFilters(q, filters, base).order(sort.key, { ascending: sort.dir === "asc" }).limit(5000);
  const { data, error } = await q;
  if (error) throw error;
  return data;
}

export async function fetchStatusCounts(filters, base) {
  const keys = ["", ...STATUSES];
  const results = await Promise.all(keys.map(async (s) => {
    let q = supabase.from("admin_profile_list").select("id", { count: "exact", head: true });
    q = applyFilters(q, filters, { ...base, status: s || undefined });
    const { count } = await q;
    return count || 0;
  }));
  return Object.fromEntries(keys.map((k, i) => [k || "all", results[i]]));
}

const rpc = async (fn, args) => {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw error;
  return data;
};

export const setStatus = (ids, status, reason = null) => rpc("admin_set_status", { p_ids: ids, p_status: status, p_reason: reason });
export const setFeatured = (id, value) => rpc("admin_set_featured", { p_id: id, p_value: value });

export async function deleteProfiles(ids) {
  const res = await rpc("admin_delete_profiles", { p_ids: ids });
  if (res.photo_paths?.length) await supabase.storage.from("profile-photos").remove(res.photo_paths);
  if (res.doc_paths?.length) await supabase.storage.from("id-documents").remove(res.doc_paths);
  return res.deleted;
}

let staffCache;
export async function staffDirectory() {
  if (!staffCache) {
    const { data } = await supabase.rpc("staff_directory");
    staffCache = Object.fromEntries((data || []).map((s) => [s.user_id, s.email]));
  }
  return staffCache;
}

export const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "");
export const fmtDateTime = (v) => (v ? new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

export const PROFILE_EXPORT_COLUMNS = [
  { key: "registration_no", label: "Reg. No" },
  { key: "full_name", label: "Name" },
  { key: "profile_for", label: "Bride/Groom" },
  { key: "age", label: "Age" },
  { key: "religion", label: "Religion" },
  { key: "caste", label: "Caste" },
  { key: "highest_qualification", label: "Education" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "status", label: "Status" },
  { key: "is_verified", label: "ID Verified", value: (r) => (r.is_verified ? "Yes" : "No") },
  { key: "created_at", label: "Registered", value: (r) => fmtDate(r.created_at) },
];
